import { defineStore } from 'pinia';
import CalendarEventsAPI from 'dashboard/api/calendarEvents';

// Calendar events — backed by the Rails API (/api/v1/accounts/:id/calendar_events).
// The store keeps a view-model with date/startTime/endTime strings (what the
// month grid + dialog use) and maps to/from the API's starts_at/ends_at datetimes.

const pad = n => String(n).padStart(2, '0');
const localDate = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localTime = d => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const toViewModel = e => {
  const starts = e.starts_at ? new Date(e.starts_at) : null;
  const ends = e.ends_at ? new Date(e.ends_at) : null;
  return {
    id: e.id,
    displayId: e.display_id,
    publicId: e.public_id,
    title: e.title,
    description: e.description || '',
    date: starts ? localDate(starts) : '',
    startTime: starts ? localTime(starts) : '',
    endTime: ends ? localTime(ends) : '',
    color: e.color || null,
    agentIds: e.agent_ids || [],
    agents: e.agents || [],
    contacts: e.contacts || [],
    contactIds: e.contact_ids || [],
    labels: e.labels || [],
  };
};

const toPayload = form => ({
  title: form.title,
  description: form.description,
  starts_at: form.date
    ? new Date(`${form.date}T${form.startTime || '00:00'}`).toISOString()
    : null,
  ends_at:
    form.date && form.endTime
      ? new Date(`${form.date}T${form.endTime}`).toISOString()
      : null,
  color: form.color,
  agent_ids: form.agentIds || [],
  contact_ids: form.contactIds || (form.contacts || []).map(c => c.id),
  labels: form.labels || [],
});

export const useCalendarStore = defineStore('calendar', {
  state: () => ({
    events: [],
  }),
  getters: {
    list: state => state.events,
    byDate: state => {
      const map = {};
      state.events.forEach(evt => {
        if (!map[evt.date]) map[evt.date] = [];
        map[evt.date].push(evt);
      });
      Object.values(map).forEach(list =>
        list.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''))
      );
      return map;
    },
    getEvent: state => id => state.events.find(e => e.id === id),
  },
  actions: {
    async fetch() {
      const { data } = await CalendarEventsAPI.get();
      this.events = (data.payload || []).map(toViewModel);
    },
    async create(payload) {
      const { data } = await CalendarEventsAPI.create(toPayload(payload));
      const event = toViewModel(data.payload);
      this.events = [...this.events, event];
      return event;
    },
    async update(id, patch) {
      const { data } = await CalendarEventsAPI.update(id, toPayload(patch));
      const event = toViewModel(data.payload);
      this.events = this.events.map(e => (e.id === id ? event : e));
      return event;
    },
    async remove(id) {
      await CalendarEventsAPI.delete(id);
      this.events = this.events.filter(e => e.id !== id);
    },
  },
});
